import { formatNumberBR } from './formatos';

/**
 * Estilos prontos, fontes e paletas do editor forense.
 *
 * O ESTILO é o que um advogado escolhe; o FORMATO é o que ele ajusta à mão
 * por cima. Um parágrafo guarda o nome do estilo e só os campos que mudaram:
 * trocar de estilo descarta os ajustes, e "Limpar formatação" volta ao Corpo
 * da peça.
 */

export type Alinhamento = 'left' | 'center' | 'right' | 'justify';
export type RecuoEspecial = 'nenhum' | 'primeira' | 'deslocado';

/** Formato de um parágrafo. Medidas de recuo em cm; espaçamento em pt. */
export interface FormatoParagrafo {
  fonte: string;
  /** pt */
  tamanho: number;
  alinhamento: Alinhamento;
  recuoEsquerdo: number;
  recuoDireito: number;
  especial: RecuoEspecial;
  /** Medida do recuo especial (primeira linha ou deslocado), em cm. */
  medida: number;
  espacoAntes: number;
  espacoDepois: number;
  entrelinha: number;
  negrito: boolean;
  italico: boolean;
}

export interface EstiloPronto extends FormatoParagrafo {
  nome: string;
  /** Uma linha que diz o que o estilo faz, para o menu. */
  resumo: string;
  /** Frase de amostra no menu, no próprio estilo. */
  amostra: string;
}

const AMOSTRA_CORPO =
  'Art. 300. A tutela de urgência será concedida quando houver elementos que evidenciem a probabilidade do direito.';

export const ESTILOS = {
  corpo: {
    nome: 'Corpo da peça', resumo: 'Arial 12 · recuo 3 cm · justificado · 1,25', amostra: AMOSTRA_CORPO,
    fonte: 'Arial', tamanho: 12, alinhamento: 'justify', recuoEsquerdo: 0, recuoDireito: 0,
    especial: 'primeira', medida: 3, espacoAntes: 0, espacoDepois: 6, entrelinha: 1.25, negrito: false, italico: false,
  },
  citacao: {
    nome: 'Citação longa', resumo: 'ABNT: recuo 4 cm · corpo 10 · simples', amostra: AMOSTRA_CORPO,
    fonte: 'Arial', tamanho: 10, alinhamento: 'justify', recuoEsquerdo: 4, recuoDireito: 0,
    especial: 'nenhum', medida: 0, espacoAntes: 6, espacoDepois: 12, entrelinha: 1, negrito: false, italico: false,
  },
  titulo: {
    nome: 'Título de seção', resumo: 'negrito · sem recuo · espaço acima', amostra: 'II – DA TUTELA DE URGÊNCIA',
    fonte: 'Arial', tamanho: 12, alinhamento: 'left', recuoEsquerdo: 0, recuoDireito: 0,
    especial: 'nenhum', medida: 0, espacoAntes: 18, espacoDepois: 6, entrelinha: 1.25, negrito: true, italico: false,
  },
  ementa: {
    nome: 'Ementa', resumo: 'recuo 6 cm · corpo 11 · itálico', amostra: AMOSTRA_CORPO,
    fonte: 'Arial', tamanho: 11, alinhamento: 'justify', recuoEsquerdo: 6, recuoDireito: 0,
    especial: 'nenhum', medida: 0, espacoAntes: 6, espacoDepois: 12, entrelinha: 1, negrito: false, italico: true,
  },
  pedido: {
    nome: 'Pedidos', resumo: 'recuo deslocado 0,75 cm · a), b), c)',
    amostra: 'a) a condenação da ré ao pagamento das diferenças vencidas, com correção e juros;',
    fonte: 'Arial', tamanho: 12, alinhamento: 'justify', recuoEsquerdo: 3, recuoDireito: 0,
    especial: 'deslocado', medida: 0.75, espacoAntes: 0, espacoDepois: 6, entrelinha: 1.25, negrito: false, italico: false,
  },
  enderecamento: {
    nome: 'Endereçamento', resumo: 'negrito · sem recuo · justificado', amostra: 'EXCELENTÍSSIMO SENHOR DOUTOR JUIZ FEDERAL',
    fonte: 'Arial', tamanho: 12, alinhamento: 'justify', recuoEsquerdo: 0, recuoDireito: 0,
    especial: 'nenhum', medida: 0, espacoAntes: 0, espacoDepois: 0, entrelinha: 1.25, negrito: true, italico: false,
  },
  legenda: {
    nome: 'Legenda de tabela', resumo: 'negrito · corpo 10 · sem recuo', amostra: 'Tabela 1 – Demonstrativo das diferenças',
    fonte: 'Arial', tamanho: 10, alinhamento: 'left', recuoEsquerdo: 0, recuoDireito: 0,
    especial: 'nenhum', medida: 0, espacoAntes: 8, espacoDepois: 2, entrelinha: 1.15, negrito: true, italico: false,
  },
} satisfies Record<string, EstiloPronto>;

export type NomeEstilo = keyof typeof ESTILOS;
export const ORDEM_ESTILOS: NomeEstilo[] = ['corpo', 'citacao', 'titulo', 'ementa', 'pedido', 'enderecamento', 'legenda'];
export const ESTILO_PADRAO: NomeEstilo = 'corpo';

/** Cinco fontes, cada uma com a métrica equivalente livre para quem não a tem instalada. */
export const FONTES: { nome: string; pilha: string }[] = [
  { nome: 'Arial', pilha: 'Arial, Arimo, "Liberation Sans", sans-serif' },
  { nome: 'Times New Roman', pilha: '"Times New Roman", Tinos, "Liberation Serif", serif' },
  { nome: 'Calibri', pilha: 'Calibri, Carlito, sans-serif' },
  { nome: 'Garamond', pilha: 'Garamond, "EB Garamond", serif' },
  { nome: 'Verdana', pilha: 'Verdana, Geneva, sans-serif' },
];

// Fonte fora da lista (estilo do escritório, HTML colado) vai entre aspas e só
// com letras, dígitos, espaço e hífen: o nome entra num `style`, e um `;` ou um
// `url(` ali viraria outra declaração.
const nomeDeFonte = (nome: string): string => String(nome).replace(/[^\p{L}\p{N} -]/gu, '').trim().slice(0, 60);

export const pilhaDaFonte = (nome: string): string => {
  const conhecida = FONTES.find((f) => f.nome === nome);
  if (conhecida) return conhecida.pilha;
  const limpo = nomeDeFonte(nome);
  return limpo ? `"${limpo}", serif` : 'serif';
};

const ALINHAMENTOS: readonly Alinhamento[] = ['left', 'center', 'right', 'justify'];
const RECUOS_ESPECIAIS: readonly RecuoEspecial[] = ['nenhum', 'primeira', 'deslocado'];
/** Faixa aceita por campo numérico (cm nos recuos, pt no tamanho e no espaçamento). */
const FAIXAS: Partial<Record<keyof FormatoParagrafo, [number, number]>> = {
  tamanho: [1, 400],
  recuoEsquerdo: [-30, 30],
  recuoDireito: [-30, 30],
  medida: [0, 30],
  espacoAntes: [0, 500],
  espacoDepois: [0, 500],
  entrelinha: [0.5, 10],
};

/**
 * Só o que é formato de verdade, com o tipo certo e dentro da faixa — o resto
 * cai. Os valores vão para o `style` do parágrafo (e, dali, para o PDF gerado
 * no servidor), e o `data-ajustes` pode vir de um HTML colado ou guardado.
 */
export function formatoSeguro(valor: unknown): Partial<FormatoParagrafo> | null {
  if (!valor || typeof valor !== 'object' || Array.isArray(valor)) return null;
  const v = valor as Record<string, unknown>;
  const f: Partial<FormatoParagrafo> = {};
  const fonte = typeof v.fonte === 'string' ? nomeDeFonte(v.fonte) : '';
  if (fonte) f.fonte = fonte;
  if (typeof v.alinhamento === 'string' && (ALINHAMENTOS as readonly string[]).includes(v.alinhamento)) {
    f.alinhamento = v.alinhamento as Alinhamento;
  }
  if (typeof v.especial === 'string' && (RECUOS_ESPECIAIS as readonly string[]).includes(v.especial)) {
    f.especial = v.especial as RecuoEspecial;
  }
  if (typeof v.negrito === 'boolean') f.negrito = v.negrito;
  if (typeof v.italico === 'boolean') f.italico = v.italico;
  (Object.keys(FAIXAS) as (keyof FormatoParagrafo)[]).forEach((k) => {
    const n = v[k];
    const [min, max] = FAIXAS[k]!;
    if (typeof n === 'number' && Number.isFinite(n)) (f as Record<string, number>)[k] = Math.min(max, Math.max(min, n));
  });
  return f;
}

export const TAMANHOS = [9, 10, 11, 12, 13, 14, 16, 18, 20, 24];
export const ENTRELINHAS: { valor: number; rotulo: string; atalho?: string }[] = [
  { valor: 1, rotulo: 'Simples', atalho: 'Ctrl+1' },
  { valor: 1.15, rotulo: '1,15' },
  { valor: 1.25, rotulo: '1,25' },
  { valor: 1.5, rotulo: '1,5 linha', atalho: 'Ctrl+5' },
  { valor: 2, rotulo: 'Duplo', atalho: 'Ctrl+2' },
];
/** Paleta curta e sóbria, própria de peça: preto, grafite, vinho, marinho, verde, âmbar. */
export const CORES_TEXTO = ['#1B1B1F', '#4A4A55', '#7A2430', '#1F3A5F', '#2F5D3A', '#8A5A00'];
export const CORES_REALCE = ['#F6E27A', '#BFE3C0', '#BCD7F2', '#F4C6D0', '#E5E5E5'];

/** Formato efetivo: o estilo, com os ajustes do parágrafo por cima. */
/* ---------- Estilos do escritório ---------- */

/**
 * Estilo criado pelo escritório (ou pela pessoa) a partir de um parágrafo:
 * um estilo pronto de base mais os campos que mudam. O app guarda e devolve;
 * o editor só desenha e aplica.
 */
export interface EstiloPersonalizado {
  /** Estável e sem espaço: vai no HTML como `data-estilo="u:<id>"`. */
  id: string;
  nome: string;
  baseadoEm: NomeEstilo;
  ajustes: Partial<FormatoParagrafo>;
  alcance: 'escritorio' | 'pessoal';
  criadoPor?: string;
  /** ISO. */
  criadoEm?: string;
}

/** Ajustes do escritório sobre os estilos prontos ("Corpo da peça" com 1,5). */
export type AjustesDosEstilosProntos = Partial<Record<NomeEstilo, Partial<FormatoParagrafo>>>;

/** De quem é o ajuste de um estilo pronto: de todo o escritório ou só de quem escreve. */
export type AlcanceDoModelo = 'escritorio' | 'pessoal';

/**
 * Os ajustes que valem, campo a campo: com o padrão do escritório obrigatório, os
 * pessoais e, por cima, os do escritório; sugerido, o contrário (o de cada um vence,
 * e o do escritório vale onde a pessoa não ajustou).
 */
export function ajustesQueValem(
  escritorio: AjustesDosEstilosProntos,
  pessoais: AjustesDosEstilosProntos,
  escritorioPrevalece = true,
): AjustesDosEstilosProntos {
  const nomes = new Set([...Object.keys(pessoais), ...Object.keys(escritorio)]) as Set<NomeEstilo>;
  const saida: AjustesDosEstilosProntos = {};
  nomes.forEach((n) => {
    saida[n] = escritorioPrevalece
      ? { ...(pessoais[n] ?? {}), ...(escritorio[n] ?? {}) }
      : { ...(escritorio[n] ?? {}), ...(pessoais[n] ?? {}) };
  });
  return saida;
}

export interface RegistroDeEstilos {
  personalizados: EstiloPersonalizado[];
  ajustesProntos: AjustesDosEstilosProntos;
}

export const PREFIXO_PERSONALIZADO = 'u:';

// Um registro por página. O formato precisa dele em lugares que não veem o
// editor (o renderHTML de um atributo, a amostra de um menu); o editor o define
// a partir das props. Dois editores na mesma página dividem o registro — e é o
// que se quer: são os estilos do mesmo escritório.
let registro: RegistroDeEstilos = { personalizados: [], ajustesProntos: {} };
export function definirRegistroDeEstilos(r: RegistroDeEstilos): void {
  registro = r;
}

const ehPronto = (e: string | null | undefined): e is NomeEstilo => !!e && e in ESTILOS;

export function estiloPersonalizado(estilo: string | null | undefined): EstiloPersonalizado | null {
  if (!estilo || !estilo.startsWith(PREFIXO_PERSONALIZADO)) return null;
  const id = estilo.slice(PREFIXO_PERSONALIZADO.length);
  return registro.personalizados.find((p) => p.id === id) ?? null;
}

/** O formato de um estilo pronto; com ou sem o ajuste do escritório por cima. */
export function formatoDoPronto(n: NomeEstilo, comAjusteDoEscritorio = true): FormatoParagrafo {
  const { nome: _n, resumo: _r, amostra: _a, ...formato } = ESTILOS[n];
  return comAjusteDoEscritorio ? { ...formato, ...(registro.ajustesProntos[n] ?? {}) } : formato;
}

/**
 * O formato de um estilo, pronto ou do escritório. `baseGuardada` é o que o
 * parágrafo trouxe no HTML (`data-estilo-base`): vale quando o estilo não existe
 * neste registro — outra pessoa, outro app — para a peça não perder a forma.
 */
export function formatoDoEstilo(estilo: string | null | undefined, baseGuardada?: Partial<FormatoParagrafo> | null): FormatoParagrafo {
  if (ehPronto(estilo)) return formatoDoPronto(estilo);
  const p = estiloPersonalizado(estilo);
  if (p) return { ...formatoDoPronto(p.baseadoEm), ...p.ajustes };
  if (baseGuardada) return { ...formatoDoPronto(ESTILO_PADRAO, false), ...baseGuardada };
  return formatoDoPronto(ESTILO_PADRAO);
}

export function nomeDoEstilo(estilo: string | null | undefined): string {
  if (ehPronto(estilo)) return ESTILOS[estilo].nome;
  return estiloPersonalizado(estilo)?.nome ?? (estilo?.startsWith(PREFIXO_PERSONALIZADO) ? 'Estilo de fora' : ESTILOS[ESTILO_PADRAO].nome);
}

export function formatoEfetivo(
  estilo: string | null | undefined,
  ajustes: Partial<FormatoParagrafo> | null | undefined,
  baseGuardada?: Partial<FormatoParagrafo> | null,
): FormatoParagrafo {
  return { ...formatoDoEstilo(estilo, baseGuardada), ...(ajustes ?? {}) };
}

/** Só os campos em que `f` difere de `base`. */
export function diferencaDeFormato(base: FormatoParagrafo, f: FormatoParagrafo): Partial<FormatoParagrafo> {
  const d: Partial<FormatoParagrafo> = {};
  (Object.keys(f) as (keyof FormatoParagrafo)[]).forEach((k) => {
    if (f[k] !== base[k]) (d as Record<string, unknown>)[k] = f[k];
  });
  return d;
}

const NOMES_ALINHAMENTO: Record<Alinhamento, string> = { left: 'à esquerda', center: 'centralizado', right: 'à direita', justify: 'justificado' };

/** "Arial 12 · recuo 3 cm · justificado · 1,25" — a linha que acompanha o estilo no menu. */
export function resumoDoFormato(f: FormatoParagrafo): string {
  const partes = [`${f.fonte} ${fmtNum(f.tamanho)}`];
  if (f.especial === 'primeira' && f.medida) partes.push(`recuo ${fmtNum(f.medida)} cm`);
  else if (f.especial === 'deslocado' && f.medida) partes.push(`deslocado ${fmtNum(f.medida)} cm`);
  else if (f.recuoEsquerdo) partes.push(`recuo ${fmtNum(f.recuoEsquerdo)} cm`);
  else partes.push('sem recuo');
  partes.push(NOMES_ALINHAMENTO[f.alinhamento], fmtNum(f.entrelinha));
  if (f.negrito) partes.push('negrito');
  if (f.italico) partes.push('itálico');
  return partes.join(' · ');
}

/** Número com vírgula decimal, sem zeros sobrando: 1,25 · 3 · 0,75 (canônico do Art. 29). */
export const fmtNum = (v: number): string =>
  formatNumberBR(v, Number.isInteger(v) ? 0 : Math.round(v * 10) === v * 10 ? 1 : 2);
